import { DocumentStatus, ContentType } from '@form-factory/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Dialog } from '@utima/ui';
import { Input, type TypedFormState } from '@utima/ui-informed';
import type { FieldState, FormApi } from 'informed';
import { memo, useCallback, useRef } from 'react';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';

import { Alert } from '@/components/Alert';
import { BasicForm } from '@/components/form/BasicForm';
import { useBoundStore } from '@/store/store';

import { useDocumentMutation } from '../../hooks/useDocumentMutation';

export type ExternalDocumentFormProps = {
  chatIds: string[];
  setOpen: (open: boolean) => void;
  limitReached: boolean;
  projectedLimitReached: boolean;
  onPendingCountChange: (count: number) => void;
};

export const AddExternalDocument = memo(function ExternalDocumentForm({
  chatIds,
  setOpen,
  limitReached,
  projectedLimitReached,
  onPendingCountChange,
}: ExternalDocumentFormProps) {
  const { t } = useLingui();
  const { create } = useDocumentMutation();
  const formApi = useRef<FormApi>(null);
  const { externalDocumentUri, setExternalDocumentUri } = useBoundStore(
    useShallow(state => ({
      externalDocumentUri: state.externalDocumentUri,
      setExternalDocumentUri: state.setExternalDocumentUri,
    })),
  );

  const handleAdd = useCallback(() => {
    formApi.current?.submitForm();
    setExternalDocumentUri(undefined);
  }, [setExternalDocumentUri]);

  const handleSubmit = async ({ values }: TypedFormState<{ uri: string }>) => {
    await create.mutateAsync({
      chats: chatIds,
      status: DocumentStatus.Ready,
      type: ContentType.ExternalDocument,
      name: values.uri,
      uri: values.uri,
      publicUrl: values.uri,
    });

    setOpen(false);
  };

  const handleInformedInputChange = useCallback(
    (fieldState: FieldState) => {
      const currentUri = fieldState.value as string;
      setExternalDocumentUri(currentUri);
      const isValid = z.string().url().safeParse(currentUri).success;
      onPendingCountChange(isValid ? 1 : 0);
    },
    [setExternalDocumentUri, onPendingCountChange],
  );

  return (
    <>
      {limitReached && (
        <Alert
          variant='warning'
          title={t`Document Limit Reached`}
          className='mb-4'
        >
          <Trans>
            You have reached the maximum document limit for your plan.
          </Trans>
        </Alert>
      )}
      <BasicForm
        formApiRef={formApi}
        onSubmit={handleSubmit}
        className='h-full max-h-[500px] min-h-0 grow space-y-4'
      >
        <Input
          name='uri'
          label={t`URL`}
          zodItemSchema={z.string().url()}
          placeholder='https://example.com/document.pdf'
          required
          onChange={handleInformedInputChange}
          defaultValue={externalDocumentUri}
        />
      </BasicForm>

      <Dialog.Footer className='z-50 flex flex-row justify-end gap-2 md:flex-row'>
        <Button
          data-cta='admin-add-external-document-add-button'
          disabled={create.isPending || limitReached || projectedLimitReached}
          loading={create.isPending}
          onClick={handleAdd}
        >
          <Trans>Add</Trans>
        </Button>
      </Dialog.Footer>
    </>
  );
});
